<template>
  <OnboardingLayout
    title="Payment Summary"
    description="Join thousands of Arnergy users all over Africa who are enjoying uninterrupted power supply with our solar solution."
    @onButtonClick="submitForm"
    imageDescription="System ownership is transferred upon meeting all lease obligations"
    :imageUrl="require('@/assets/img/female-wave.gif')"
    :canContinue="!showCustomLoading && canContinue"
    :loading="showLoading"
    :isLVILocation="isFixedRateLocation"
  >
    <div class="summary-container">
      <div class="summary">
        <el-form
          ref="form"
          :rules="{
            numInverters: [
              {
                required: true,
                message: 'Please enter the Number of Systems',
                trigger: 'blur',
              },
              { validator: validateNumInverters, trigger: 'blur' },
            ],
            installationAddress: [
              {
                required: true,
                message: 'Please enter the installation address',
                trigger: 'blur',
              },
            ],
            leaseTenure: [
              {
                required: true,
                message:
                  'Please select the closest installation address region',
                trigger: 'change',
              },
            ],
            buildingType: [
              {
                required: true,
                message: 'Please select the building type',
                trigger: 'change',
              },
            ],
          }"
          :model="formData"
        >
          <div class="grid">
            <el-form-item
              label="Number of Systems"
              prop="numInverters"
                :style="canSeePricingModel ? '' : 'grid-column: 1 / -1'"
            >
              <el-input
                placeholder="Number of Systems"
                v-model="formData.numInverters"
                @input="checkCanContinue"
                type="number"
              ></el-input>
            </el-form-item>

            <!-- The select field should only show for when isFixedRateLocation is false -->
            <template v-if="canSeePricingModel">
              <el-form-item label="Pricing Model " prop="pricingModel">
                <el-select
                  v-model="formData.pricingModel"
                  placeholder="Select Pricing Model"
                  @change="checkCanContinue"
                >
                  <el-option
                    label="Incremental"
                    value="incremental"
                  ></el-option>
                  <el-option
                    label="Decremental"
                    value="decremental"
                  ></el-option>
                  <!-- <el-option label="Fixed" value="fixed"></el-option> -->
                </el-select>
                <a-tooltip placement="top">
                  <template slot="title">
                    This determines your payment structure. With the incremental
                    option, payments start low and increase by a set percentage
                    each month, ending with the highest payment in the final
                    month. The decremental option works in reverse, starting
                    with the highest payment and decreasing each month.
                    <a-icon
                      type="info-circle"
                      style="margin-left: 5px; cursor: help"
                    />
                  </template>
                </a-tooltip>
              </el-form-item>
            </template>
          </div>
          <el-form-item label="Installation Address" prop="installationAddress">
            <el-input
              v-model="formData.installationAddress"
              @change="checkCanContinue"
            ></el-input>
          </el-form-item>
          <div class="grid">
            <el-form-item
              label="Building Type"
              prop="buildingType"
              :style="!isFixedRateLocation ? 'grid-column: 1 / -1' : ''"
            >
              <el-select
                v-model="formData.buildingType"
                placeholder="Select building type"
                @change="checkCanContinue"
                style="width: 100%"
              >
                <el-option
                  label="Storage Only"
                  value="storage_only"
                ></el-option>
                <el-option label="Bungalow" value="bungalow"></el-option>
                <el-option label="1 Storey" value="1_storey"></el-option>
                <el-option label="2 Storey" value="2_storey"></el-option>
              </el-select>
            </el-form-item>
            <!-- <el-form-item label="Building Type" prop="buildingType"
            :style="isFixedRateLocation ? '' : 'grid-column: 1 / -1'"
            >
              <el-select
                v-model="formData.buildingType"
                placeholder="Select building type"
                @change="checkCanContinue"
              >
                <el-option
                  label="Storage Only"
                  value="storage_only"
                ></el-option>
                <el-option label="Bungalow" value="bungalow"></el-option>
                <el-option label="1 Storey" value="1_storey"></el-option>
                <el-option label="2 Storey" value="2_storey"></el-option>
              </el-select>
            </el-form-item> -->
            <template v-if="canSeeLeaseTenure">
              <el-form-item label="Lease Tenure(years)" prop="leaseTenure">
                <el-select
                  v-model="formData.leaseTenure"
                  filterable
                  placeholder="Select"
                  @change="checkCanContinue"
                >
                  <el-option
                    v-for="duration in durations"
                    :key="duration.key"
                    :label="duration.value"
                    :value="duration.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </div>
        </el-form>

        <div class="cost-loading" v-if="showCustomLoading">
          Calculating cost, please wait... <a-spin />
        </div>

        <div class="summary-table">
          <div>
            <p>Total 1st Month Payment (payable now)</p>
            <p>
              NGN
              {{ (isFixedRateLocation && referralname && referralname.toLowerCase() !== "sunfi")
                || (referralname === null && LVI_REGIONS.includes(customerRegion.toLowerCase()))
                ? 0 : Intl.NumberFormat("en-US").format(firstPaymentAmt) }}
            </p>
          </div>
          <div>
            <p>Starting Monthly Payment</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(firstPaymentAmt) }}
            </p>
          </div>
          <div v-show="yearlyPayment.length != 0">
            <p><strong>Yearly Payment</strong></p>
            <p>
              <strong>Amount</strong>
            </p>
          </div>
          <div v-for="(value, index) in yearlyPayment" :key="index">
            <p>Year {{ index + 1 }}</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(value) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </OnboardingLayout>
</template>

<script>
import OnboardingLayout from "@/components/layouts/onboarding-layout.vue";
import durationsData from "@/data/durations.json";
import fixedRateLocations from "@/data/fixed-rate-location.json";
import ippLocationsData from "@/data/ipp-location.json";
import locationsData from "@/data/location.json";
import CalculatorService from "@/services/calculator.js";
import OnboardingService from "@/services/onboarding.js";
import { Icon, Tooltip } from "ant-design-vue";
import { Form, FormItem } from "element-ui";
import { mapGetters } from "vuex";

export default {
  components: {
    OnboardingLayout,
    "el-form": Form,
    "el-form-item": FormItem,
    ATooltip: Tooltip,
    AIcon: Icon,
  },
  computed: {
    ...mapGetters(["getUserSession"]),
    isFixedRateLocation() {
      const result= fixedRateLocations.some(
        (location) =>
          location.key ===
          this.$store.state.onboardingCustomerState.customerRegion
      );
      return result;
    },
  },
  created() {
    this.locations = locationsData;
    this.ippLocations = ippLocationsData;
    this.durations = durationsData;
  },
  data() {
    return {
      showLoading: false,
      yearlyPayment: [],
      canContinue: false,
      addToWaitList: false,
      showCustomLoading: false,
      nextPageName: "BVNCheck",
      formData: {
        numInverters: 1,
        installationAddress: "",
        buildingType: "",
        leaseTenure: "",
        pricingModel: "",
      },
      firstPaymentAmt: 0,
      activePackage: {
        cautionFee: 0,
        monthlyPaymentValue: 0,
      },
      onboardingStateId: null,
      canSeePricingModel: false,
      canSeeLeaseTenure: false,
      LVI_REGIONS: ["lekki", "victoria_island", "ikoyi"],
      referralname: null,
      customerRegion: null,
    };
  },
  methods: {
    checkCanContinue() {
      const { numInverters, installationAddress, buildingType, leaseTenure } =
        this.formData;
      // this.determineNextPage();
      this.calculateFirstPayment();
      if (
        (this.firstPaymentAmt > 0 &&
          numInverters &&
          installationAddress &&
          buildingType) ||
        leaseTenure
      ) {
        this.canContinue = true;
      } else {
        this.canContinue = false;
      }
    },
    validateNumInverters(rule, value, callback) {
      if (value < 1) {
        callback(new Error("Number of Systems must be at least 1"));
      } else {
        callback();
      }
    },
    // determineNextPage() {
    //   this.addToWaitList = !this.ippLocations.find(
    //     (x) => x.key == this.formData.leaseTenure
    //   );
    // },

    async calculateFirstPayment() {
      const {referralname, customerRegion} = await this.getCurrentState();
      console.log(
        "this.formData.pricingModel",
        this.formData.pricingModel,
        this.isFixedRateLocation,
        this.formData.leaseTenure
      );

      if (!this.formData.pricingModel && !this.isFixedRateLocation) return;
      try {
        this.showCustomLoading = true;
        const notSunfiReferral =
          this.isFixedRateLocation && (referralname && referralname.toLowerCase() !== "sunfi") ||
          (referralname === null && this.LVI_REGIONS.includes(customerRegion.toLowerCase()))
        const res = await CalculatorService.ippFirstMonthPayment({
          arnergyProductId: this.activePackage.id,
          numInverters: this.formData.numInverters,
          pricingModel: notSunfiReferral
            ? "fixed"
            : this.formData.pricingModel,
          selected_duration: notSunfiReferral
            ? this.formData.leaseTenure
            : 5,
        });
        this.firstPaymentAmt = res.data.data;
        this.yearlyPayment = res.data.yearly_breakdown;
        this.showCustomLoading = false;
      } catch (error) {
        this.showCustomLoading = false;
      }
    },
    async updateDBData(payloadData) {
      try {
        this.showLoading = true;

        if (this.addToWaitList) {
          await OnboardingService.addToWaitList(); // add to waitlist
        }
        // if (this.isExistingUser) {
        //   this.nextPageName = "OnboardContractUpload";
        // } else {
        //   this.nextPageName = "BVNCheck";
        // }

        const res = await OnboardingService.updateDbData({
          ...payloadData,
          currentPageName: this.nextPageName,
          onboardingStateId: this.onboardingStateId,
        });
        this.showLoading = false;
        if (res) {
          this.navTo(this.nextPageName);
        }
      } catch (error) {
        console.log({ error });
        this.showLoading = false;
      }
    },

    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          if (!this.canSeePricingModel) {
            this.formData.pricingModel = "fixed";
            this.formData.selected_duration = this.formData.leaseTenure;
          }
          this.updateDBData({
            ...this.formData,
            product_id: this.activePackage.id,
            customer_id: this.$store.state.onboardingCustomerState.customerid,
          });
        } else {
          // Form validation failed
        }
      });
    },

    async getCurrentState() {
      const HTTP = this.$httpUtils.FASTHTTP;
      const url = "/onboardingstate/state";
      try {
        const response = await HTTP.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("appUserToken")}`,
          },
        });
        this.referralname = response.data.referralname;
        this.customerRegion = response.data.customerRegion;
        return response.data;
      } catch (error) {
        console.log({ error });
      }
    },

   
    async fetchVisibilityState() {
      const state = await this.getCurrentState();
      
      if (!state) return;

      const { referralname, customerRegion } = state;

      const hasNoReferral = referralname === null && !this.LVI_REGIONS.includes(customerRegion)
      const isSunfiReferral =
        referralname && referralname.toLowerCase() === "sunfi";
      
      this.canSeePricingModel = hasNoReferral || isSunfiReferral;
      this.canSeeLeaseTenure = !hasNoReferral && !isSunfiReferral;
      
      console.log("canSeePricingModel", this.canSeePricingModel);
      console.log("canSeeLeaseTenure", this.canSeeLeaseTenure);
    },
  },
  async mounted() {
    this.activePackage =
      this.$store.state.onboardingCustomerState.selectedProduct;
    this.onboardingStateId = localStorage.getItem("onboardingStateId");
    await this.fetchVisibilityState();
  },
};
</script>

<style lang="scss" scoped>
h1,
h2,
h3,
h4,
p {
  margin: 0;
  padding: 0;
  font-family: "DM Sans", sans-serif;
  font-weight: 400;
}

.downPayment {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}
.tenureMonth {
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}

.buildingType {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}

.address {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
    // font-weight: normal;
  }
}

.quantity label {
  font-size: 16px;
  color: #727272;
  // font-weight: normal;
}
.quantity input {
  width: 50px;
  border-radius: 6px;
  height: 30px;
  margin-left: 6px;
  border: 1px solid #d3d9dd;
}

p {
  color: #727272;
  font-size: 16px;
  line-height: 24px;
}

h2 {
  color: #0892d0;
  margin-bottom: 18px;
}

.summary-container {
  width: 100%;
  max-width: 527px;
}

.summary {
  // width: 55%;

  h2 {
    color: #0892d0;
    margin-bottom: 30px;
    font-weight: 700;
  }

  > div {
    width: 100%;
  }

  .summary-table {
    padding: 0px 28px;
    background: #fafafa;

    > div {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 17px 0px;
      border-bottom: 1px dashed #e8e2e2;

      p {
        color: #000000;
        font-size: 16px;
      }

      p:last-child {
        text-align: left;
        width: 30%;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    .bold-text p {
      font-weight: bold;
    }

    .tabbed {
      position: relative;
    }

    .tabbed p {
      margin-left: 55px;
    }

    .tabbed .arrows {
      position: absolute;
      bottom: 30px;
      left: 25px;
    }
  }

  .tenure-section {
    margin-bottom: 30px;

    h4 {
      color: #fe7a11;
      margin-bottom: 10px;
      font-weight: bold;
    }

    .tenure-bar {
      display: flex;
      justify-content: space-between;

      .point {
        text-align: center;
        position: relative;
        width: 15px;
        overflow-x: visible;
        cursor: pointer;

        &:first-child {
          cursor: text;
        }

        &:last-child {
          text-align: right;
        }

        p {
          color: #efefef;
          font-size: 12px;
          line-height: 16px;
          margin-bottom: 2px;
          height: 20px;
        }

        div {
          width: 3px;
          height: 8px;
          border-radius: 2px;
          background: #efefef;
          display: inline-block;
        }

        div.active {
          background: #ace5ff;
        }

        p.marked {
          padding: 2px 8px;
          border-radius: 5px;
          background: #0892d0;
          width: 80px;
          margin-left: -32.5px;
        }

        div.marked {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #0892d0;
        }
      }
    }

    .base-line {
      background: #efefef;
      height: 5px;
      border-radius: 9px;
      margin-top: -14px;

      .blue-line {
        width: 82.5%;
        height: 5px;
        background: #ace5ff;
        border-radius: 9px;
      }
    }
  }
}

.cost-loading {
  display: grid;
  height: 100px;
  place-content: center;
}

.illustration {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 350px;

  img:first-child {
    margin-bottom: -3rem;
    z-index: 3;
  }
}

.description {
  max-width: 340px;
  border-radius: 40px;
  padding: 32px 20px;
  background: #e6f6ff;
  text-align: center;
  font-family: "DM Sans";
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #0892d0;
}

.polygon {
  width: 5.3rem;
  height: 5.8rem;
  margin-left: 21rem;
  margin-top: -1.5rem;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.fixed-rate-message {
  color: #0892d0;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: #e6f6ff;
  border-radius: 4px;
  margin-bottom: 15px;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/onboarding/ipp/payment-summary/index.vue